import React, { useEffect, useState, useMemo } from 'react';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  Button,
  Card,
  Grid,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Toolbar,
  Tooltip,
  IconButton,
  LinearProgress,
  Checkbox
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AppSnackbar from '../../../common/AppSnackbar';
import StatusLabel from '../../../common/StatusLabel';
import TrainingAssignDialog from '../dialogs/TrainingAssignDialog';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';

import {
  useGetTrainingUsers,
  useDeleteTrainingUsers
} from '../TrainingUser.hooks';

import { useGetTrainingVersions } from '../Training.hooks';
import { formatDate } from '../../../common/formatter';

export interface TrainingTabAssignProps {
  trainingId: number;
}

const TrainingTabAssign: React.FC<TrainingTabAssignProps> = (
  props: TrainingTabAssignProps
) => {
  const { trainingId } = props;
  const [status, setStatus] = useQueryParam('status', StringParam);
  const [trainingVersionId, setTrainingVersionId] = useQueryParam(
    'trainingVersionId',
    withDefault(NumberParam, undefined)
  );
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order] = useQueryParam('order', StringParam);
  const [orderDirection] = useQueryParam('direction', StringParam);

  const [openDialog, setOpenDialog] = useState(false);
  const { trainingVersions } = useGetTrainingVersions(trainingId, true);
  const { trainingUsers, isLoading, error, get } =
    useGetTrainingUsers(trainingId);
  const [selectedUsers, setSelectedUser] = useState([]) as any;
  const [openDeleteUserDialog, setDeleteUserDialog] = useState(false);
  const { deletedUsers, isDeleting, del } = useDeleteTrainingUsers(
    trainingId,
    trainingVersionId || 0
  );

  const params = useMemo(() => {
    const params = {
      page: Number(page) || 0,
      limit: Number(limit) || 100,
      order: String(order || '')
    } as any;
    if (order && orderDirection) {
      params.order = `${order} ${orderDirection}`;
    }
    if (trainingVersionId) {
      params.trainingVersionId = trainingVersionId;
    }
    if (status) {
      params.status = status;
    }
    return params;
  }, [limit, order, orderDirection, page, status, trainingVersionId]);

  useEffect(() => {
    get(params);
  }, [get, params]);

  useEffect(() => {
    if (!isDeleting) {
      get(params);
    }
    setSelectedUser([]);
  }, [isDeleting, deletedUsers, setSelectedUser, get, params]);

  useEffect(() => {
    if (trainingVersions && trainingVersions.length > 0) {
      setTrainingVersionId(trainingVersions[0].id);
    }
  }, [setTrainingVersionId, trainingVersions]);

  function isUserSelected(trainingUserId: number | undefined) {
    return selectedUsers.includes(trainingUserId);
  }

  function handleSelectUser(event: any, trainingUserId: number | undefined) {
    if (event.target.checked) {
      setSelectedUser([...selectedUsers, trainingUserId]);
    } else {
      setSelectedUser(
        selectedUsers.filter((n: number) => n !== trainingUserId)
      );
    }
  }

  function removeSelectedUsers() {
    del({ userIds: selectedUsers.join(',') });
  }
  interface EnhancedTableToolbarProps {
    numSelected: number;
  }

  const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    const { numSelected } = props;

    return (
      <Toolbar>
        {numSelected > 0 ? (
          <Typography
            style={{ flex: '1 1 100%' }}
            color='textSecondary'
            variant='subtitle1'
          >
            {numSelected} selected
          </Typography>
        ) : (
          <Typography style={{ flex: '1 1 100%' }} variant='h6' id='tableTitle'>
            Employees
          </Typography>
        )}
        {numSelected > 0 ? (
          <Tooltip title='Delete' onClick={() => setDeleteUserDialog(true)}>
            <IconButton aria-label='delete'>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <div></div>
        )}
      </Toolbar>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              onClick={() => setOpenDialog(true)}
            >
              Assign an Employee
            </Button>
            {openDialog && (
              <TrainingAssignDialog
                trainingId={trainingId}
                trainingVersionId={trainingVersionId}
                open={openDialog}
                onCancel={() => setOpenDialog(false)}
                onAssigned={() => {
                  setOpenDialog(false);
                  get(params); //update data
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Card>
              {/* <CardTitle>Filter</CardTitle>
                <Divider /> */}
              <List>
                <ListItem dense divider>
                  {/* <ListItemIcon>
                    <FilterListIcon />
                  </ListItemIcon> */}
                  <Typography variant='overline'>Filter</Typography>
                </ListItem>
                <ListItem
                  button
                  selected={!status}
                  onClick={() => setStatus(undefined)}
                >
                  <ListItemText>All</ListItemText>
                </ListItem>
                <ListItem
                  button
                  selected={status === 'to-do'}
                  onClick={() => setStatus('to-do')}
                >
                  <ListItemText>Not Started</ListItemText>
                </ListItem>
                <ListItem
                  button
                  selected={status === 'in-progress'}
                  onClick={() => setStatus('in-progress')}
                >
                  <ListItemText>In Progress</ListItemText>
                </ListItem>
                <ListItem
                  button
                  selected={status === 'completed'}
                  onClick={() => setStatus('completed')}
                >
                  <ListItemText>Completed</ListItemText>
                </ListItem>
              </List>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <List>
                <ListItem dense divider>
                  <Typography variant='overline'>Training Versions</Typography>
                </ListItem>
                {trainingVersions &&
                  trainingVersions.map(version => (
                    <ListItem
                      key={version.id}
                      button
                      selected={version.id === trainingVersionId}
                      onClick={() => setTrainingVersionId(version.id)}
                    >
                      <ListItemText>Version: {version.version}</ListItemText>
                    </ListItem>
                  ))}
              </List>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={9}>
        <Card>
          <EnhancedTableToolbar numSelected={selectedUsers.length} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'></TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {trainingUsers &&
                  trainingUsers.data.map(trainingUser => (
                    <TableRow key={trainingUser.id} hover onClick={() => {}}>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          color='primary'
                          checked={isUserSelected(trainingUser.user.id)}
                          onChange={event =>
                            handleSelectUser(event, trainingUser.user.id)
                          }
                        />
                      </TableCell>
                      <TableCell>{trainingUser.user.firstName}</TableCell>
                      <TableCell>{trainingUser.user.lastName}</TableCell>
                      <TableCell>
                        {trainingUser.user.groups
                          ?.map(group => group.name)
                          .join(', ')}
                      </TableCell>
                      <TableCell>
                        {formatDate(trainingUser.assignedDate)}
                      </TableCell>
                      <TableCell>
                        {trainingUser.dueDate &&
                          formatDate(trainingUser.dueDate)}
                      </TableCell>
                      <TableCell>
                        <StatusLabel status={trainingUser.status} />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={trainingUsers?.total || 0}
            rowsPerPage={limit || 100}
            page={page || 0}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
          <AppSnackbar severity='error' open={Boolean(error)}>
            There was an error loading the list of users.
          </AppSnackbar>
        </Card>
      </Grid>

      {openDeleteUserDialog && (
        <ConfirmDialog
          open={openDeleteUserDialog}
          title={`Please Confirm`}
          message='Would you like to remove the selected users from this training?'
          onCancel={() => {
            setDeleteUserDialog(false);
          }}
          onConfirm={() => {
            setDeleteUserDialog(false);
            removeSelectedUsers();
          }}
        />
      )}
    </Grid>
  );
};

export default TrainingTabAssign;
